import { MESSAGE_TYPE } from 'gcs-common/constants/MessageTypes';
import { resizeToDimensions } from 'gcs-common/helper/imageHelper';
import { calculateMessageMediaURL } from 'gcs-common/helper/mediaHelper';
import brokenImage from 'gcs-common/img/broken_image.svg';
import { getIsChatConnected } from 'gcs-common/slices/chatConnection/chatConnectionSelectors';
import {
  getMessageAttributesText,
  getMessageChannelId,
  getMessageDateCreated,
  getMessageHasSyncPending,
  getMessageImgDimensions,
  getMessageMediaFilename,
  getMessageMediaHumanFilesize,
  getMessageMediaIsImage,
  getMessageMediaRelativePath,
  getMessageThumbnail,
  getMessageType,
  getPendingMediaMessageLocalFile,
} from 'gcs-common/slices/messages/messageSelector';
import downloadMedia from 'gcs-common/slices/messages/messagesThunks/downloadMedia';
import messageUpdateImageDimensions from 'gcs-common/slices/messages/messagesThunks/messageUpdateImageDimensions';
import openMessageMediaFullscreen from 'gcs-common/slices/messages/messagesThunks/openMessageMediaFullscreen';
import { getCurrentUserIsMemberOfChannel } from 'gcs-common/slices/salesSpaceChannels/salesSpaceChannelsSelectors';
import { type ElementType, type SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CubeIcon from '../../img/cube_primary.svg?react';
import FileOutline from '../../img/file_outline.svg?react';
import { showDisabledNotification } from '../../slices/notifications/ShowNotificationThunk';
import LocalFilePreview from '../LocalFilePreview/LocalFilePreview';
import styles from './styles.module.scss';

interface MediaMessageProps {
  messageId: string;
  maxHeight?: number;
  maxWidth?: number;
  selectionDisabled?: boolean;
  CustomTextComponent?: ElementType;
}

const MediaMessage = (props: MediaMessageProps) => {
  const { messageId, selectionDisabled = false, maxHeight = 300, maxWidth = 300, CustomTextComponent = null } = props;
  const messageType = useSelector(getMessageType(messageId));
  const dateCreated = useSelector(getMessageDateCreated(messageId));
  const isImage = useSelector(getMessageMediaIsImage(messageId));
  const text = useSelector(getMessageAttributesText(messageId));
  const mediaThumbnail = useSelector(getMessageThumbnail(messageId));
  const filename = useSelector(getMessageMediaFilename(messageId));
  const humanFileSize = useSelector(getMessageMediaHumanFilesize(messageId));
  const imgDimensions: { w: number; h: number } | undefined = useSelector(getMessageImgDimensions(messageId));
  const mediaRelativePath = useSelector(getMessageMediaRelativePath(messageId));
  const channelId = useSelector(getMessageChannelId(messageId));
  const isJoinedChannel = useSelector(getCurrentUserIsMemberOfChannel(channelId));
  const pendingLocalFile = useSelector(getPendingMediaMessageLocalFile(messageId));
  const messageHasSyncPending = useSelector(getMessageHasSyncPending(messageId));
  const isChatConnected = useSelector(getIsChatConnected);

  const [failedMediaUrl, setFailedMediaUrl] = useState<string>();

  const dispatch = useDispatch();

  // The media URL is static, so the <img> can render right away instead of waiting for the chat connection
  const mediaUrl = useMemo(
    () =>
      isImage && mediaRelativePath && !messageHasSyncPending ? calculateMessageMediaURL(mediaRelativePath) : undefined,
    [isImage, mediaRelativePath, messageHasSyncPending],
  );
  const mediaDownloadFailed = !!mediaUrl && failedMediaUrl === mediaUrl;

  const formattedDateCreated = useMemo(() => dateCreated.format('L'), [dateCreated]);

  const onDownloadMedia = useCallback(async () => {
    if (selectionDisabled) {
      return;
    }
    if (!messageHasSyncPending) {
      // @ts-expect-error redux
      dispatch(downloadMedia({ messageId }));
    } else {
      dispatch(showDisabledNotification('Nachricht wird gerade synchronisiert.'));
    }
  }, [selectionDisabled, dispatch, messageId, messageHasSyncPending]);

  const onImageClicked = useCallback(async () => {
    if (selectionDisabled) {
      return;
    }
    dispatch(openMessageMediaFullscreen({ messageId }));
  }, [selectionDisabled, dispatch, messageId]);

  const onImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement, Event>) => {
      if (!isImage || imgDimensions) {
        return;
      }
      const { naturalWidth, naturalHeight } = event.currentTarget;

      dispatch(
        // @ts-expect-error redux
        messageUpdateImageDimensions({
          messageId,
          width: naturalWidth,
          height: naturalHeight,
        }),
      );
    },
    [dispatch, imgDimensions, isImage, messageId],
  );

  const onImageError = useCallback(() => {
    setFailedMediaUrl(mediaUrl);
  }, [mediaUrl]);

  // Retry a failed image once the chat is connected again (e.g. it failed while offline)
  useEffect(() => {
    if (isChatConnected) {
      setFailedMediaUrl(undefined);
    }
  }, [isChatConnected]);

  const { display: displayDimensions } = resizeToDimensions({
    width: imgDimensions?.w,
    height: imgDimensions?.h,
    maxWidth,
    maxHeight,
  });

  const style = {
    width: `${displayDimensions.width || 50}px`,
    height: `${displayDimensions.height || 50}px`,
    maxWidth: `${maxWidth}px`,
    maxHeight: `${maxHeight}px`,
  };

  if (!isImage) {
    const downloadDesc = filename || 'Datei herunterladen';
    return (
      <div className={styles.mediaDownloadWrapper}>
        {mediaThumbnail ? (
          <img src={mediaThumbnail} className={styles.previewImage} alt="preview-placeholder" />
        ) : (
          <FileOutline className={styles.previewImage} />
        )}
        <div className={styles.mediaInfo}>
          <div role="button" tabIndex={0} className={styles.downloadLinkButton} onClick={onDownloadMedia}>
            <span className={styles.downloadLink}>
              {CustomTextComponent ? <CustomTextComponent text={downloadDesc} /> : downloadDesc}
            </span>
            &nbsp; [{humanFileSize}]
          </div>
          {text && (
            <div className={styles.messageText}>{CustomTextComponent ? <CustomTextComponent text={text} /> : text}</div>
          )}
          {messageType === MESSAGE_TYPE.MEDIA_FROM_MAIL && (
            <div className={styles.dateTag}>
              &nbsp;
              {formattedDateCreated}
            </div>
          )}
        </div>
      </div>
    );
  }

  if ((mediaUrl && !mediaDownloadFailed) || pendingLocalFile) {
    return (
      <>
        <div
          role="button"
          tabIndex={0}
          className={`${styles.imgButton} ${!isJoinedChannel && !selectionDisabled ? styles.disabled : ''}`}
          onClick={onImageClicked}
        >
          {pendingLocalFile ? (
            <LocalFilePreview
              alt="Bild aus Nachricht"
              file={pendingLocalFile}
              className={styles.img}
              data-testid={`message-image-${filename}`}
            />
          ) : (
            <img
              alt="Bild aus Nachricht"
              className={styles.img}
              src={mediaUrl}
              style={
                displayDimensions.width && displayDimensions.height
                  ? { width: `${displayDimensions.width}px`, height: `${displayDimensions.height}px` }
                  : undefined
              }
              onLoad={onImageLoad}
              onError={onImageError}
              data-testid={`message-image-${filename}`}
            />
          )}
        </div>
        {text && (
          <div className={styles.messageText}>{CustomTextComponent ? <CustomTextComponent text={text} /> : text}</div>
        )}
      </>
    );
  }

  if (mediaDownloadFailed) {
    return (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100px',
          }}
        >
          <img alt="Bild aus Nachricht" src={brokenImage} />
        </div>
        <div className={styles.messageText}>Mediendateien in Vorschau nicht verfügbar</div>
      </>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 'auto',
          ...style,
        }}
      >
        <CubeIcon className={styles.imgLoader} />
      </div>
      {text && (
        <div className={styles.messageText}>{CustomTextComponent ? <CustomTextComponent text={text} /> : text}</div>
      )}
    </>
  );
};

export default MediaMessage;
